'use client';
import React from 'react';
import { appPublicConfig } from '@/configs/appPublicConfig';
import { BasicLayout } from '@/shared/components/layouts/BasicLayout';
import { InformationActionsPage } from '@/shared/components/pages/InformationActionsPage';
import { BigBar } from '@/shared/components/parts/BigBar';
import type { UserInfo } from '@/shared/hooks/useUserInfoAndMenus';
import { useUserInfoAndMenus } from '@/shared/hooks/useUserInfoAndMenus';
import type { FormType } from '../../constants';

type AuthZState =
  | 'INIT'
  | 'PREPARED'
  | 'REJECT_CAUSE_BY_B2B'
  | 'REJECT_CAUSE_BY_CHILD_ACCOUNT';

export const InquiryAuthZGuard: React.FC<
  React.PropsWithChildren & {
    formType: FormType;
  }
> = ({ children, formType }) => {
  const [authZState, setAuthZState] = React.useState<AuthZState>('INIT');

  const { isLoadedUserInfo, userInfo } = useUserInfoAndMenus();

  React.useEffect(() => {
    if (!isLoadedUserInfo) {
      return;
    }

    if (userInfo.isLoggedIn && isB2B2CUser(userInfo)) {
      setAuthZState('REJECT_CAUSE_BY_B2B');
      return;
    }

    if (
      userInfo.isLoggedIn &&
      !userInfo.isMainAccount &&
      formType === 'hnext'
    ) {
      setAuthZState('REJECT_CAUSE_BY_CHILD_ACCOUNT');
      return;
    }

    setAuthZState('PREPARED');
  }, [isLoadedUserInfo, userInfo, formType]);

  switch (authZState) {
    case 'INIT':
      return <BasicLayout options={appPublicConfig} />;

    case 'PREPARED':
      return <>{children}</>;

    case 'REJECT_CAUSE_BY_B2B':
      return (
        <BasicLayout options={appPublicConfig}>
          <BigBar title="お問い合わせ" />
          <InformationActionsPage
            title="ご案内"
            texts={
              <div>
                現在ログインされているアカウントについてのお問い合わせは、専用窓口へご連絡いただく必要があります。
                <br />
                詳しくは以下のページをご確認ください。
              </div>
            }
            actions={[
              {
                uiType: 'linkPrimary',
                href: `${appPublicConfig.HELP}/partner-support-desk`,
                text: 'お問い合わせ窓口のご案内',
              },
            ]}
          />
        </BasicLayout>
      );

    case 'REJECT_CAUSE_BY_CHILD_ACCOUNT':
      return (
        <BasicLayout options={appPublicConfig}>
          <BigBar title="お問い合わせ" />
          <InformationActionsPage
            title="こちらの機能は利用できません"
            texts={
              <div>
                サブアカウントでは利用できません。
                <br />
                アカウントメニューから、メインアカウントに切り替えてお試しください。
              </div>
            }
            actions={[
              {
                uiType: 'linkPrimary',
                href: `${appPublicConfig.MYACCOUNT}/settings`,
                text: 'アカウントメニュー',
              },
            ]}
          />
        </BasicLayout>
      );
  }
};

function isB2B2CUser(userInfo: UserInfo) {
  return (
    userInfo.isYamadaPF ||
    userInfo.isWith ||
    userInfo.isBBM ||
    userInfo.isTSUTAYA ||
    userInfo.otherFunctionId === '100' ||
    userInfo.otherFunctionId === '102' ||
    userInfo.otherFunctionId === 'USEN'
  );
}
